/**
 * Solicitudes: lo que llega del chip NFC y del portal.
 *
 * Cinco toques de pánico producen **un** ticket: la deduplicación por hora es
 * una restricción de la base, no un chequeo de esta pantalla.
 *
 * Acá vive también el selector de **a quién se le avisa** (P5.3). Está en esta
 * pantalla y no en el perfil de cada empleado porque la pregunta que contesta
 * no es «¿cómo es esta persona?» sino «cuando entre una solicitud, ¿quién se
 * entera?» — y esa se responde mirando la lista entera, no una ficha a la vez.
 */

import {
  REQUEST_CATEGORY_LABEL, REQUEST_STATUS_LABEL, REQUEST_STATUS_TONE,
  type RequestCategory, type RequestStatus,
} from '@cleanblue/shared';
import { api } from '@/lib/api';
import { useApi, useApiMutation } from '@/lib/hooks';
import {
  Async, Badge, Button, Card, Cell, Empty, Note, PageHead, StatusBadge, Table,
} from '@/components/ui';

interface Request {
  id: string; unit_code: string | null; unit_name: string | null;
  category: RequestCategory; note: string | null; contact_phone: string | null;
  priority: string; status: RequestStatus; source: string; created_at: string;
  assigned_name: string | null;
}

interface StaffNotification {
  employee_id: string; full_name: string; channel: string;
  /** Si hay teléfono cargado — el número no viaja hasta acá. */
  phone: boolean;
  enabled: boolean;
}

interface StaffNotifications { staff: StaffNotification[]; reachable: number; at_risk: boolean }

/**
 * El selector de avisos, en manos del administrador.
 *
 * Tres cosas que no son obvias y son decisiones, no descuidos:
 *
 *   1. **Apagar cubre también las emergencias.** Se dice en la pantalla porque
 *      es lo contrario de lo que uno esperaría de un interruptor de avisos.
 *   2. **Cero destinatarios avisa, no bloquea.** Si el admin apaga al último,
 *      el cambio se guarda y aparece la advertencia. Un bloqueo duro se sortea
 *      desactivando a esa persona por otra vía, y el sistema quedaría igual sin
 *      nadie: lo que hay que cubrir es el descuido.
 *   3. **La lista casi vacía es el estado real del negocio.** Hoy en producción
 *      hay dos empleados —el de prueba y el perfil del dueño— y ninguno de
 *      limpieza. Por eso el vacío se explica en vez de parecer una falla.
 */
function StaffNotificationsCard() {
  const query = useApi<StaffNotifications>('/admin/staff-notifications');

  const toggle = useApiMutation(
    ({ id, enabled }: { id: string; enabled: boolean }) =>
      api.patch(`/admin/staff-notifications/${id}`, { enabled }),
    ['/admin/staff-notifications'],
  );

  return (
    <Card
      title="A quién se le avisa"
      footer={
        <span className="small muted">
          El personal no puede apagarse el aviso a sí mismo: lo decide el administrador.
        </span>
      }
    >
      <p className="small muted" style={{ marginTop: 0 }}>
        Cuando entra una solicitud se le avisa a todo el personal activo que lo tenga encendido.
        Apagarlo deja a esa persona sin recibir <strong>nada</strong>, emergencias incluidas, y su
        teléfono deja de salir del sistema.
      </p>

      {toggle.error && <Note tone="danger">{toggle.error.message}</Note>}

      <Async query={query}>
        {(data) => (
          <>
            {data.at_risk && (
              <Note tone="danger">
                Nadie puede recibir avisos. Una emergencia no le va a llegar a ninguna persona
                hasta que enciendas al menos una.
              </Note>
            )}

            {data.staff.length === 0 ? (
              <Empty
                icon="personal" title="No hay personal activo"
                hint="Apenas se cargue personal de limpieza va a aparecer acá con su interruptor."
              />
            ) : (
              <div style={{ display: 'grid', gap: 6, marginTop: 10 }}>
                {data.staff.map((s) => (
                  <label key={s.employee_id} className="row small">
                    <input
                      type="checkbox"
                      checked={s.enabled}
                      disabled={toggle.isPending}
                      onChange={(e) => toggle.mutate({ id: s.employee_id, enabled: e.target.checked })}
                    />
                    <span className="strong">{s.full_name}</span>
                    <span className="spacer" />
                    {!s.phone && (
                      <Badge tone="warn" plain title="Sin teléfono cargado no hay a dónde escribirle">
                        sin teléfono
                      </Badge>
                    )}
                    <span className="muted">{s.channel}</span>
                  </label>
                ))}
              </div>
            )}
          </>
        )}
      </Async>
    </Card>
  );
}

export function RequestsPage() {
  const query = useApi<{ requests: Request[] }>('/admin/requests');

  const setStatus = useApiMutation(
    ({ id, status }: { id: string; status: RequestStatus }) =>
      api.post(`/admin/requests/${id}/status`, { status }),
    ['/admin/requests', '/admin/dashboard'],
  );

  return (
    <>
      <PageHead
        title="Solicitudes"
        subtitle="Llegan del chip NFC del consultorio o del portal del cliente."
      />

      <div className="mb-3">
        <StaffNotificationsCard />
      </div>

      <Async query={query} empty={(d) => d.requests.length === 0 && (
        <Empty icon="calidad" title="No hay solicitudes" hint="Van a aparecer acá apenas alguien pida atención." />
      )}>
        {(data) => (
          <Card flush>
            <Table head={
              <tr>
                <th>Consultorio</th><th>Solicitud</th><th>Origen</th>
                <th>Prioridad</th><th>Estado</th><th />
              </tr>
            }>
              {data.requests.map((r) => (
                <tr key={r.id}>
                  <td className="strong">
                    <Cell sub={r.created_at.slice(0, 16).replace('T', ' ')}>{r.unit_code ?? '—'}</Cell>
                  </td>
                  <td>
                    <Cell sub={r.note ?? r.contact_phone}>{REQUEST_CATEGORY_LABEL[r.category]}</Cell>
                  </td>
                  <td className="small muted">{r.source}</td>
                  <td>
                    <Badge tone={r.priority === 'URGENTE' ? 'danger' : r.priority === 'ALTA' ? 'warn' : 'neutral'}>
                      {r.priority}
                    </Badge>
                  </td>
                  <td>
                    <StatusBadge value={r.status} labels={REQUEST_STATUS_LABEL} tones={REQUEST_STATUS_TONE} />
                  </td>
                  <td className="right">
                    {r.status === 'OPEN' && (
                      <Button disabled={setStatus.isPending}
                        onClick={() => setStatus.mutate({ id: r.id, status: 'DISPATCHED' })}>
                        Despachar
                      </Button>
                    )}
                    {(r.status === 'DISPATCHED' || r.status === 'ACKNOWLEDGED') && (
                      <Button variant="primary" disabled={setStatus.isPending}
                        onClick={() => setStatus.mutate({ id: r.id, status: 'RESOLVED' })}>
                        Resolver
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </Table>
          </Card>
        )}
      </Async>
    </>
  );
}
